import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import useCurrentUser from "../hooks/useCurrentUser";
import * as XLSX from "xlsx";
import {
  ClipboardList,
  Search,
  Download,
  ArrowUpDown,
  Calendar,
  Filter,
  FileText,
  User,
  Activity
} from "lucide-react";

export default function ClientHistory({ materialId = null, onClose = () => { } }) {
  const { clientId } = useCurrentUser();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterType, setFilterType] = useState("ALL"); // For global history

  const isModal = !!materialId;

  // Fetch logs
  useEffect(() => {
    if (!clientId) return;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        let q;
        const historyRef = collection(db, "activityLogs");
        if (isModal) {
          // Inventory specific history
          q = query(historyRef, where("companyId", "==", clientId), where("target", "==", "inventory"), where("materialId", "==", materialId), orderBy("timestamp", "desc"), limit(100));
        } else {
          // Global system history
          q = query(historyRef, where("companyId", "==", clientId), orderBy("timestamp", "desc"), limit(200));
        }

        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLogs(data);
      } catch (err) {
        console.error("Error fetching logs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [materialId, clientId, isModal]);

  // Filter & Sort
  const filteredLogs = useMemo(() => {
    return logs
      .filter((l) => {
        // Type Filter (Global only)
        if (!isModal && filterType !== "ALL" && l.type !== filterType) return false;

        const s = searchTerm.toLowerCase();
        // Search fields
        return (
          (l.action?.toLowerCase() || "").includes(s) ||
          (l.userName?.toLowerCase() || "").includes(s) ||
          (l.details?.toLowerCase() || "").includes(s) ||
          (l.note?.toLowerCase() || "").includes(s) ||
          (l.docId?.toLowerCase() || "").includes(s)
        );
      })
      .sort((a, b) => {
        const tA = a.timestamp?.seconds || 0;
        const tB = b.timestamp?.seconds || 0;
        return sortOrder === "asc" ? tA - tB : tB - tA;
      });
  }, [logs, searchTerm, sortOrder, filterType, isModal]);

  const exportToExcel = () => {
    const dataToExport = filteredLogs.map((l) => ({
      Date: l.timestamp ? new Date(l.timestamp.seconds * 1000).toLocaleString() : "N/A",
      Module: l.type || "General",
      Action: l.action,
      Ref_ID: l.docId || l.material_no || "-",
      User: l.userName || l.role || "Unknown",
      Note: l.note || "",
      Details: typeof l.details === 'object' ? JSON.stringify(l.details) : (l.details || "")
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "History");
    XLSX.writeFile(wb, `History_${isModal ? materialId : 'Global'}.xlsx`);
  };

  // --- RENDER HELPERS ---
  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'inventory': return 'bg-blue-100 text-blue-800';
      case 'order': return 'bg-purple-100 text-purple-800';
      case 'shipment': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const Content = (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col ${isModal ? 'max-h-[90vh]' : 'min-h-[600px]'}`}>

      {/* Header */}
      <div className={`bg-navy-900 text-white p-6 flex justify-between items-center ${!isModal ? 'rounded-t-xl' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="bg-white/10 p-2 rounded-lg">
            <Activity size={24} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-serif">{isModal ? "Movement History" : "System Audit Log"}</h2>
            <p className="text-navy-100 text-sm opacity-80">
              {isModal ? `Ref: ${materialId}` : "Comprehensive Transaction History"}
            </p>
          </div>
        </div>
        {isModal && (
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
          >
            &times;
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex gap-4 w-full md:w-auto">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
              placeholder={isModal ? "Search movements..." : "Search Ref ID, Actions, Users..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {!isModal && (
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="pl-9 pr-8 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 appearance-none bg-white cursor-pointer"
              >
                <option value="ALL">All Modules</option>
                <option value="inventory">Inventory</option>
                <option value="order">Orders</option>
                <option value="shipment">Shipments</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <button
            onClick={() => setSortOrder(prev => prev === "desc" ? "asc" : "desc")}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <ArrowUpDown size={16} /> {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 shadow-sm transition-colors"
          >
            <Download size={16} /> Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto bg-slate-50 p-4">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-900 mr-3"></div>
            Loading logs...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <ClipboardList size={48} className="mb-4 opacity-20" />
            <p>No records found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="min-w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-navy-900 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3">Date & Time</th>
                  {!isModal && <th className="px-6 py-3">Module</th>}
                  <th className="px-6 py-3">Action</th>
                  {!isModal && <th className="px-6 py-3">Reference</th>}
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Details/Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-3 text-sm text-slate-600 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-slate-400" />
                        {log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString() : "N/A"}
                      </div>
                    </td>

                    {!isModal && (
                      <td className="px-6 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${getTypeColor(log.type)}`}>
                          {log.type || 'System'}
                        </span>
                      </td>
                    )}

                    <td className="px-6 py-3">
                      <span className="font-medium text-navy-900 text-sm">
                        {log.action}
                      </span>
                    </td>

                    {!isModal && (
                      <td className="px-6 py-3 text-sm text-slate-600 font-mono">
                        {log.docId || "-"}
                      </td>
                    )}

                    <td className="px-6 py-3 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-slate-400" />
                        {log.role || "User"}
                      </div>
                    </td>

                    <td className="px-6 py-3 text-sm text-slate-500 max-w-sm truncate" title={log.note || (typeof log.details === 'string' ? log.details : JSON.stringify(log.details))}>
                      {log.note || (log.details && typeof log.details === 'object' ? Object.keys(log.details).join(', ') : log.details) || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer (Modal Only) */}
      {isModal && (
        <div className="p-4 border-t border-slate-200 bg-white flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="w-full max-w-4xl">
          {Content}
        </div>
      </div>
    );
  }

  // Page layout
  return (
    <div className="max-w-7xl mx-auto pb-12">
      {Content}
    </div>
  );
}
